(() => {
/* Each showcase contains at most eight server-rendered products; no catalog API calls. */
class SJProductCarousel extends HTMLElement {
  connectedCallback() {
    this.cleanup?.abort();
    this.cleanup = new AbortController();
    const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.cleanup.signal });
    this.slides = [...this.querySelectorAll('[data-sj-slide]')];
    if (this.slides.length < 2) return;
    this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
    this.visible = false;
    this.hovered = false;
    this.focused = false;
    this.interval = Math.max(4000, Math.min(12000, Number(this.dataset.interval) || 6000));
    this.querySelector('[data-sj-controls]').hidden = false;
    this.pauseButton = this.querySelector('[data-sj-pause]');
    on(this, 'click', event => {
      if (event.target.closest('[data-sj-prev]')) this.step(-1, true);
      if (event.target.closest('[data-sj-next]')) this.step(1, true);
      if (event.target.closest('[data-sj-pause]')) { this.paused = this.pauseIntent ?? !this.paused; this.pauseIntent = undefined; this.update(); }
    });
    on(this, 'keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.step(event.key === 'ArrowLeft' ? -1 : 1, true);
    });
    on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
    on(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
    // Focus pauses rotation until the shopper explicitly presses play.
    on(this, 'focusin', () => { this.focused = true; this.paused = true; this.update(); });
    on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } });
    on(this, 'pointerdown', event => {
      if (event.target.closest('[data-sj-pause]')) this.pauseIntent = !this.paused;
      if (event.pointerType !== 'mouse') this.touch = { x: event.clientX, y: event.clientY };
    });
    on(this, 'pointerup', event => {
      if (!this.touch) return;
      const dx = event.clientX - this.touch.x, dy = event.clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) { this.suppressClick = true; this.step(dx < 0 ? 1 : -1, true); }
    });
    on(this, 'pointercancel', () => { this.touch = null; });
    on(this, 'click', event => { if (this.suppressClick) { event.preventDefault(); this.suppressClick = false; } });
    on(document, 'visibilitychange', () => this.schedule());
    on(this.motion, 'change', () => { if (this.motion.matches) this.paused = true; this.update(); });
    on(document, 'shopify:section:select', event => { if (event.target.contains(this)) { this.paused = true; this.update(); } });
    this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, { threshold: 0.15 });
    this.observer.observe(this);
    this.update();
  }
  step(direction, manual = false) {
    if (manual) this.paused = true;
    const focusInSlide = this.slides[this.index].contains(document.activeElement);
    this.slides[this.index].hidden = true;
    this.index = (this.index + direction + this.slides.length) % this.slides.length;
    this.slides[this.index].hidden = false;
    if (focusInSlide) this.slides[this.index].querySelector('a')?.focus({ preventScroll: true });
    this.querySelector('[data-sj-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
    if (manual) this.querySelector('[data-sj-status]').textContent = `${this.index + 1} / ${this.slides.length}: ${this.slides[this.index].querySelector('img').alt}`;
    this.update();
  }
  update() {
    this.pauseButton.setAttribute('aria-label', this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
    this.querySelector('[data-sj-pause-icon]').textContent = this.paused ? '▷' : 'Ⅱ';
    this.schedule();
  }
  schedule() {
    clearTimeout(this.timer);
    if (!this.paused && !this.hovered && !this.focused && this.visible && !document.hidden) this.timer = setTimeout(() => this.step(1), this.interval);
  }
  disconnectedCallback() { clearTimeout(this.timer); this.observer?.disconnect(); this.cleanup?.abort(); }
}
if (!customElements.get('sj-product-carousel')) customElements.define('sj-product-carousel', SJProductCarousel);

// Integrates with Dawn's authoritative gallery without replacing variants, zoom or media.
class SJProductRotation extends HTMLElement {
  async connectedCallback() {
    await customElements.whenDefined('media-gallery');
    if (!this.isConnected || this.closest('quick-add-modal')) return;
    this.gallery = this.closest('media-gallery');
    if (!this.gallery?.dataset.desktopLayout.includes('thumbnail')) return;
    this.items = [...this.gallery.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]')].filter(item => item.querySelector('.product__modal-opener--image') && !item.classList.contains('product__media-item--variant'));
    if (this.items.length < 2) return;
    this.hidden = false;
    this.cleanup = new AbortController();
    const on = (target, type, fn) => target.addEventListener(type, fn, {signal:this.cleanup.signal});
    this.button = this.querySelector('button');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
    this.interval = Math.max(4000, Math.min(12000, Number(this.dataset.interval) || 6000));
    this.visible = false;
    this.hovered = false;
    const product = this.closest('product-info') || this.gallery;
    on(product, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
    on(product, 'pointerleave', () => { this.hovered = false; this.schedule(); });
    on(product, 'focusin', () => { this.paused = true; this.update(); });
    on(product, 'pointerdown', event => { if (!this.contains(event.target)) { this.paused = true; this.update(); } });
    on(this.button, 'pointerdown', () => { this.pauseIntent = !this.paused; });
    on(this.button, 'click', () => { this.paused = this.pauseIntent ?? !this.paused; this.pauseIntent = undefined; this.update(); });
    on(document, 'visibilitychange', () => this.schedule());
    on(this.motion, 'change', () => { if (this.motion.matches) { this.paused = true; this.update(); } });
    this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, {threshold:.2});
    this.observer.observe(this.gallery.querySelector('[id^="GalleryViewer"]'));
    this.update();
  }
  update() { this.button.textContent = this.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel; this.schedule(); }
  schedule() {
    clearTimeout(this.timer);
    if (this.paused || this.hovered || !this.visible || document.hidden) return;
    this.timer = setTimeout(() => {
      this.items = [...this.gallery.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]')].filter(item => item.querySelector('.product__modal-opener--image') && !item.classList.contains('product__media-item--variant'));
      const index = this.items.findIndex(item => item.classList.contains('is-active'));
      if (index === -1 || document.querySelector('product-modal[open], quick-add-modal[open]')) { this.schedule(); return; }
      this.gallery.setActiveMedia(this.items[(index + 1) % this.items.length].dataset.mediaId, false, false);
      this.schedule();
    }, this.interval);
  }
  disconnectedCallback() { clearTimeout(this.timer); this.observer?.disconnect(); this.cleanup?.abort(); }
}
if (!customElements.get('sj-product-rotation')) customElements.define('sj-product-rotation', SJProductRotation);

})();
